import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import SiteFooter from "../components/SiteFooter";
import logo from "../assets/logo.png";

const publicInformation = {
  manifesto: {
    title: "Manifesto",
    copy: "Blackspace is not a billboard. It is a social realm for private drafts, public rooms, real messages, thoughtful profiles, and people who want signal without surrendering the self.",
  },
  privacy: {
    title: "Privacy",
    copy: "Privacy is a promise, not permission to harm. No doxxing, impersonation, spam, threats, or targeted abuse. The Patron can protect the realm, but private messages stay private unless a user reports them.",
  },
  terms: {
    title: "Terms",
    copy: "Blackspace is built for real people, lawful speech, and accountable public rooms. Use the realm without attacking identities, exploiting private information, or poisoning the spaces others rely on.",
  },
} as const;

export default function PublicInfoPage({ page }: { page: keyof typeof publicInformation }) {
  const { title, copy } = publicInformation[page];

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} · Blackspace`;
    return () => { document.title = previousTitle; };
  }, [title]);

  return (
    <div className="relative isolate flex min-h-[100dvh] flex-col overflow-hidden bg-[#03050d] text-slate-100">
      <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true" style={{ background: "radial-gradient(ellipse at 50% 8%, rgba(91, 65, 175, .15), transparent 43%), radial-gradient(ellipse at 88% 72%, rgba(34, 211, 238, .045), transparent 35%)" }} />
      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-5 py-5 sm:px-8 sm:py-7">
        <Link to="/" className="inline-flex min-h-11 items-center gap-3 rounded-md text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200" aria-label="Blackspace home">
          <img src={logo} alt="" className="h-9 w-9 rounded-md border border-cyan-200/20 object-cover" />
          <span className="text-sm font-bold tracking-tight text-white sm:text-base">Blackspace</span>
        </Link>
        <Link to="/" className="inline-flex min-h-11 items-center gap-2 rounded-md px-2 text-xs font-semibold text-slate-400 transition hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200 sm:text-sm">
          <ArrowLeft size={16} aria-hidden="true" /> Back to Blackspace
        </Link>
      </header>

      <main className="relative z-10 mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center px-6 pb-16 pt-10 sm:px-8 sm:pb-24">
        <p className="mb-4 text-xs font-semibold tracking-[.18em] text-violet-300/75">Blackspace</p>
        <h1 className="text-4xl font-semibold tracking-tight text-white sm:text-5xl">{title}</h1>
        <div className="mt-7 h-px w-16 bg-gradient-to-r from-cyan-300/70 to-violet-400/70" aria-hidden="true" />
        <p className="mt-8 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg sm:leading-9">{copy}</p>
      </main>

      <SiteFooter currentPage={page} />
    </div>
  );
}
